import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Toast } from '../components/ui/Toast';
import { ArrowLeft, Save, Trash2 } from 'lucide-react';
import { getResortAdminUser, updateResortAdminUser, deleteResortAdminUser, getResortLocations, listRestaurants, listGroceries, listResortAdminRoles } from '../services/resortAdminApi';
import { useAuth } from '../context/AuthContext';

interface UserDetails {
  name: string;
  email: string;
  role: string;
  status: 'Active' | 'Disabled';
  scope_type: 'restaurant' | 'grocery' | 'all';
  restaurants?: string[];
  groceries?: string[];
  last_login?: string;
}

export const UserDetails: React.FC = () => {
  const { name } = useParams<{ name: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [resortLocations, setResortLocations] = useState<Array<{ value: string; label: string }>>([]);
  const [restaurants, setRestaurants] = useState<Array<{ name: string; restaurant_name: string }>>([]);
  const [groceries, setGroceries] = useState<Array<{ name: string; grocery_name: string }>>([]);
  const [availableRoles, setAvailableRoles] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    role: 'Partner Admin',
    status: 'Active' as 'Active' | 'Disabled',
    scope_type: 'all' as 'restaurant' | 'grocery' | 'all',
    restaurants: [] as string[],
    groceries: [] as string[],
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Load resort locations, restaurants, groceries, and roles
  useEffect(() => {
    const loadData = async () => {
      try {
        const locationsResult = await getResortLocations();
        if (locationsResult && locationsResult.resort_locations) {
          setResortLocations(locationsResult.resort_locations);
        }

        const restaurantsResult = await listRestaurants(undefined, 1, 1000);
        if (restaurantsResult && restaurantsResult.data) {
          setRestaurants(restaurantsResult.data);
        }

        const groceriesResult = await listGroceries(undefined, 1, 1000);
        if (groceriesResult && groceriesResult.data) {
          setGroceries(groceriesResult.data);
        }

        // Load available roles from server (already excludes Resort Super Admin)
        const rolesResult = await listResortAdminRoles();
        if (rolesResult && Array.isArray(rolesResult)) {
          setAvailableRoles(rolesResult);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };
    loadData();
  }, []);

  // Load user details
  useEffect(() => {
    const loadUser = async () => {
      if (!name) return;
      setLoading(true);
      try {
        const result = await getResortAdminUser(name);
        setUserDetails(result);
        setFormData({
          role: result.role as 'Resort Super Admin' | 'Partner Admin' | 'Partner Courier' | 'Support',
          status: result.status as 'Active' | 'Disabled',
          scope_type: result.scope_type as 'restaurant' | 'grocery' | 'all',
          restaurants: result.restaurants || [],
          groceries: result.groceries || [],
        });
      } catch (error: any) {
        setError(error.message || 'Failed to load user details');
      } finally {
        setLoading(false);
      }
    };
    loadUser();
  }, [name]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name) return;
    
    setSaving(true);
    setError('');
    setSuccess('');

    if (formData.role !== 'Support' && formData.scope_type === 'restaurant' && formData.restaurants.length === 0) {
      setError('At least one restaurant is required');
      setSaving(false);
      return;
    }
    if (formData.role !== 'Support' && formData.scope_type === 'grocery' && formData.groceries.length === 0) {
      setError('At least one grocery is required');
      setSaving(false);
      return;
    }
    if (formData.role !== 'Support' && formData.scope_type === 'all' && formData.restaurants.length === 0 && formData.groceries.length === 0) {
      setError('At least one restaurant or grocery is required');
      setSaving(false);
      return;
    }

    try {
      await updateResortAdminUser({
        name,
        role: formData.role !== 'Resort Super Admin' ? formData.role : undefined,
        status: formData.status,
        scope_type: formData.scope_type,
        restaurants: formData.role !== 'Support' && (formData.scope_type === 'restaurant' || formData.scope_type === 'all') ? formData.restaurants : undefined,
        groceries: formData.role !== 'Support' && (formData.scope_type === 'grocery' || formData.scope_type === 'all') ? formData.groceries : undefined,
      });
      setSuccess('User updated successfully');
      setToast({ message: 'User updated successfully', type: 'success' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      // Reload user details
      const result = await getResortAdminUser(name);
      setUserDetails(result);
    } catch (error: any) {
      const errMsg = error.message || 'Failed to update user';
      setError(errMsg);
      setToast({ message: errMsg, type: 'error' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!name || !userDetails) return;
    
    if (userDetails.email === user?.email) {
      setError('You cannot delete your own account');
      setShowDeleteConfirm(false);
      return;
    }
    
    setDeleting(true);
    setError('');
    
    try {
      await deleteResortAdminUser(name);
      navigate('/settings');
    } catch (error: any) {
      setError(error.message || 'Failed to delete user');
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading user details...</div>
      </div>
    );
  }

  if (!userDetails) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-red-500">User not found</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/settings')}
            className="text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-800">Edit User</h1>
            <p className="mt-1 text-sm text-gray-600">{userDetails.email}</p>
          </div>
        </div>
        {userDetails.email !== user?.email && (
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="inline-flex items-center px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete User
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4">
          <p className="text-sm text-green-700">{success}</p>
        </div>
      )}

      {showDeleteConfirm && (
        <Card>
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Confirm Delete</h3>
            <p className="text-sm text-gray-600">
              Are you sure you want to delete user <strong>{userDetails.email}</strong>? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </Card>
      )}

      <form onSubmit={handleSave} className="space-y-6">
        <Card>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                value={userDetails.email}
                disabled
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-50 text-gray-500 sm:text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">Email cannot be changed</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Role</label>
              <select
                value={formData.role}
                onChange={(e) => {
                  const selectedRole = e.target.value;
                  // Automatically set scope_type based on role
                  let newScopeType: 'restaurant' | 'grocery' | 'all' = formData.scope_type;
                  let newRestaurants = formData.restaurants;
                  let newGroceries = formData.groceries;
                  if (selectedRole === 'Partner Admin' || selectedRole === 'Partner Courier' || selectedRole === 'Resort Super Admin' || selectedRole === 'Support') {
                    newScopeType = 'all';
                  }
                  setFormData({
                    ...formData,
                    role: selectedRole,
                    scope_type: newScopeType,
                    restaurants: newRestaurants,
                    groceries: newGroceries,
                  });
                }}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
              >
                {availableRoles.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Status</label>
              <select
                value={formData.status}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    status: e.target.value as 'Active' | 'Disabled'
                  })
                }
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
              >
                <option value="Active">Active</option>
                <option value="Disabled">Disabled</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Scope Type</label>
              <select
                value={formData.scope_type}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    scope_type: e.target.value as 'restaurant' | 'grocery' | 'all',
                    restaurants: e.target.value === 'grocery' ? [] : formData.restaurants,
                    groceries: e.target.value === 'restaurant' ? [] : formData.groceries,
                  })
                }
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                disabled={formData.role === 'Partner Admin' || formData.role === 'Partner Courier' || formData.role === 'Resort Super Admin' || formData.role === 'Support'}
              >
                <option value="restaurant">Restaurant</option>
                <option value="grocery">Grocery</option>
                {(formData.role === 'Resort Super Admin' || formData.role === 'Partner Admin' || formData.role === 'Partner Courier' || formData.role === 'Support') && (
                  <option value="all">All (Restaurants & Groceries)</option>
                )}
              </select>
              {(formData.role === 'Partner Admin' || formData.role === 'Partner Courier' || formData.role === 'Resort Super Admin' || formData.role === 'Support') && (
                <p className="mt-1 text-xs text-gray-500">Scope type is automatically set based on the selected role</p>
              )}
            </div>

            {(formData.scope_type === 'restaurant' || formData.scope_type === 'all') && formData.role !== 'Support' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Restaurants (you can select multiple) <span className="text-red-500">*</span></label>
                <div className="mt-1 border border-gray-300 rounded-md shadow-sm max-h-48 overflow-y-auto p-2">
                  {restaurants.map((restaurant) => (
                    <label key={restaurant.name} className="flex items-center py-1 px-2 hover:bg-gray-50 rounded">
                      <input
                        type="checkbox"
                        className="mr-2 h-4 w-4 text-alps-primary focus:ring-alps-primary border-gray-300 rounded"
                        checked={formData.restaurants.includes(restaurant.name)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({
                              ...formData,
                              restaurants: [...formData.restaurants, restaurant.name],
                            });
                          } else {
                            setFormData({
                              ...formData,
                              restaurants: formData.restaurants.filter((r) => r !== restaurant.name),
                            });
                          }
                        }}
                      />
                      <span className="text-sm text-gray-700">
                        {restaurant.restaurant_name || restaurant.name}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {(formData.scope_type === 'grocery' || formData.scope_type === 'all') && formData.role !== 'Support' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Groceries (you can select multiple) <span className="text-red-500">*</span></label>
                <div className="mt-1 border border-gray-300 rounded-md shadow-sm max-h-48 overflow-y-auto p-2">
                  {groceries.map((grocery) => (
                    <label key={grocery.name} className="flex items-center py-1 px-2 hover:bg-gray-50 rounded">
                      <input
                        type="checkbox"
                        className="mr-2 h-4 w-4 text-alps-primary focus:ring-alps-primary border-gray-300 rounded"
                        checked={formData.groceries.includes(grocery.name)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({
                              ...formData,
                              groceries: [...formData.groceries, grocery.name],
                            });
                          } else {
                            setFormData({
                              ...formData,
                              groceries: formData.groceries.filter((g) => g !== grocery.name),
                            });
                          }
                        }}
                      />
                      <span className="text-sm text-gray-700">
                        {grocery.grocery_name || grocery.name}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {userDetails.last_login && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Last Login</label>
                <input
                  type="text"
                  value={new Date(userDetails.last_login).toLocaleString()}
                  disabled
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-50 text-gray-500 sm:text-sm"
                />
              </div>
            )}
          </div>
        </Card>

        <div className="flex justify-end space-x-2">
          <button
            type="button"
            onClick={() => navigate('/settings')}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-alps-primary hover:bg-alps-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-alps-primary disabled:opacity-50"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

